import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ESTIMATED_GENERATION_DURATION_MS } from "@/config/constants";

const MAX_PROGRESS = 95;
const TICK_INTERVAL_MS = 1000;

interface ProgressStep {
  threshold: number;
  labelKey: "progressPreparing" | "progressGenerating" | "progressEnhancing" | "progressAlmostReady";
}

const PROGRESS_STEPS: ProgressStep[] = [
  { threshold: 15, labelKey: "progressPreparing" },
  { threshold: 60, labelKey: "progressGenerating" },
  { threshold: 85, labelKey: "progressEnhancing" },
  { threshold: MAX_PROGRESS, labelKey: "progressAlmostReady" },
];

function getStepLabel(
  progress: number,
  generateTranslations: Record<string, string>,
): string {
  for (const step of PROGRESS_STEPS) {
    if (progress < step.threshold) {
      return generateTranslations[step.labelKey] ?? "";
    }
  }
  return generateTranslations[PROGRESS_STEPS[PROGRESS_STEPS.length - 1].labelKey] ?? "";
}

/**
 * Asymptotic progress curve: approaches MAX_PROGRESS but never exceeds it.
 * Uses exponential decay: progress = max * (1 - e^(-elapsed / tau))
 * tau is tuned so progress ≈ 90% at estimatedDurationMs.
 */
function calculateProgress(elapsedMs: number, estimatedDurationMs: number): number {
  // tau = -estimatedDuration / ln(1 - 0.9) ≈ estimatedDuration / 2.302
  const tau = estimatedDurationMs / 2.302;
  const raw = MAX_PROGRESS * (1 - Math.exp(-elapsedMs / tau));
  return Math.min(Math.round(raw), MAX_PROGRESS);
}

interface UseEstimatedProgressOptions {
  startTime: string;
  mediaType: "image" | "video";
}

interface EstimatedProgress {
  progress: number;
  label: string;
}

/**
 * Elapsed since the generation started, measured purely from the server's
 * `created_at`: `max(0, Date.now() − createdAt)`. Stateless, so it is IDENTICAL
 * on every refresh / flow-switch / new tab / mobile tab-discard / device — the
 * progress never resets to 0. (The `max(0, …)` guard only stops a device clock
 * running behind the server from rendering a negative-width bar.)
 */
function elapsedSince(serverStartMs: number): number {
  return Math.max(0, Date.now() - serverStartMs);
}

export function useEstimatedProgress({
  startTime,
  mediaType,
}: UseEstimatedProgressOptions): EstimatedProgress {
  const { t } = useLanguage();
  const estimatedDurationMs = ESTIMATED_GENERATION_DURATION_MS[mediaType];
  const startMs = useRef(new Date(startTime).getTime());

  const [progress, setProgress] = useState<number>(() =>
    calculateProgress(elapsedSince(startMs.current), estimatedDurationMs),
  );

  useEffect(() => {
    startMs.current = new Date(startTime).getTime();
  }, [startTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(
        calculateProgress(elapsedSince(startMs.current), estimatedDurationMs),
      );
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [estimatedDurationMs]);

  const label = getStepLabel(progress, t.generate as Record<string, string>);

  return { progress, label };
}
